import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMutation } from '@apollo/client'
import { debounce } from 'lodash'
import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import { ApiTax } from '@vend/types'
import { add, divide, multiply, subtract, vn } from 'vend-number'
import { AMERICAN_STATES, US_COUNTRY_CODE } from '../../../../vend-ui/constants'
import { useRegisterManager, useSaleManager } from '../../../../ng-compat/hooks'
import { DEBOUNCE_OPTIONS } from '../../../../constants/debounce-time'
import { FulfilmentType } from '../../../../models/fulfilment'
import { LineItem, LineItemTaxComponent } from '../../../../models/line-item'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  selectLineItems,
  selectSaleFulfilmentDetail,
  selectAutoTaxCalculating,
  selectAutoTaxLastCalculatedKey,
  selectSaleFinals,
  selectPreAutoTaxFinals,
  selectPreAutoTaxLineItems,
  selectCalculateTaxError,
  selectAutoTaxInactiveCategoryLineItemIds,
  selectSaleDisplayDiscount,
  selectSaleDiscountIsPercentage,
  setAutoTaxCalculating,
  setAutoTaxLastCalculatedKey,
  setPreAutoTaxFinals,
  setPreAutoTaxLineItems,
  setCalculateTaxError,
  setAutoTaxInactiveCategoryLineItemIds,
  updateLineItems,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  AddressErrorsStatus,
  useFulfillmentHelper,
} from '../../sale-line-item/fulfilment/popover/hooks/useFulfillmentHelper'
import { getStateCode } from '../../sale-line-item/fulfilment/popover/utils/addressHelper'

import {
  CalculateTax,
  CalculateTaxErrorTypes,
  CalculateTaxInput,
  CalculateTaxResponse,
  ResponseLineItem,
  TaxAddress,
  TaxLineItem,
} from '../../../../queries/calculate-tax'
import { selectActiveOutletId } from '../../../../state/slices/posState/posStateSlice'
import { store } from '../../../../state/store'
import { TaxesStore } from '../../../../factories/database/taxes-store'
import { syncManager } from '../../../../ng-compat/managers'
import { useProductTaxCodes } from './useProductTaxCodes'
import { useAutotaxSettings } from './useAutotaxSettings'
import { useCustomerTaxes } from './useCustomerTaxes'

const FREIGHT_BRAND_NAME = 'Freight'

const isFreightLineItem = (item: LineItem): boolean =>
  item.product?.brand?.name === FREIGHT_BRAND_NAME

export type AutoTax = {
  isAutoTaxApplied: boolean
  isLoading: boolean
  hasCalculateTaxError: boolean
  inactiveCategoryLineItemIds?: string[]
}

const taxesStore = new TaxesStore()

/**
 * Ensures that tax groups referenced in the CalculateTax response exist in IndexedDB.
 * The CalculateTax mutation may create new tax groups on the server that don't exist locally yet.
 * If any are missing, triggers a sync to fetch them.
 */
const syncTaxGroupsIfMissing = async (
  responseLineItems: ResponseLineItem[]
) => {
  try {
    const groupRateIds = new Set(
      responseLineItems.flatMap(item => item.taxes.map(tax => tax.groupRateId))
    )

    const lookups = await Promise.all(
      [...groupRateIds].map(id => taxesStore.get(id).then(tax => ({ id, tax })))
    )

    const hasMissing = lookups.some(({ tax }) => !tax)

    if (hasMissing) {
      syncManager.sync()
    }
  } catch {
    // Non-critical: sync will eventually pick up new tax groups
  }
}

/**
 * Applies a customer's custom tax override to delivery line items that
 * don't already have it. Returns the updated line items to dispatch,
 * or an empty array if no updates are needed.
 */
const applyCustomerTaxToLineItems = (
  customerTax: ApiTax | undefined,
  lineItems: LineItem[]
): LineItem[] => {
  if (!customerTax) {
    return []
  }

  const deliveryItemsNeedingCustomerTax = lineItems.filter(
    item =>
      item.fulfilmentType === FulfilmentType.DELIVERY &&
      !item.customerTaxApplied
  )

  if (deliveryItemsNeedingCustomerTax.length === 0) {
    return []
  }

  return deliveryItemsNeedingCustomerTax.map(item => ({
    ...item,
    adjusted: true,
    customerTaxApplied: true,
    taxInfo: customerTax,
    taxComponents: customerTax.rates.map(rate => ({
      rate_id: rate.id,
      total_tax: 0,
      rate,
      total_tax_after_sale_discount: 0,
      base_total_tax: 0,
    })),
  }))
}

const mapAddressToTaxAddress = (address: AddressDetails): TaxAddress => ({
  countryCode: address.country_id,
  postCode: address.postcode,
  stateCode: getStateCode(address.state, address.country_id),
  city: address.city,
  street: `${address.address_1} ${address.address_2 || ''}`.trim(),
})

/**
 * Computes the sale-level discount percentage as a decimal.
 * For percentage discounts (e.g. 10%), returns 0.1.
 * For dollar discounts (e.g. $5), divides by the total discountable price
 * to get a proportional percentage.
 */
const calculateSaleDiscountPercent = (
  saleDisplayDiscount: string | number,
  saleDiscountIsPercentage: boolean,
  items: ReturnType<typeof selectLineItems>
): number => {
  if (!saleDisplayDiscount || vn(saleDisplayDiscount).eq(0)) {
    return 0
  }

  if (saleDiscountIsPercentage) {
    return divide(saleDisplayDiscount, 100)
  }

  // For dollar discounts, compute the proportion relative to the total
  // discountable price across ALL items (not just delivery)
  const totalDiscountablePrice = items.reduce(
    (total, item) => add(total, multiply(item.price, item.quantity)),
    0
  )

  if (vn(totalDiscountablePrice).eq(0)) {
    return 0
  }

  return divide(saleDisplayDiscount, totalDiscountablePrice)
}

export const buildTaxInput = (
  details: ReturnType<typeof selectSaleFulfilmentDetail>,
  items: ReturnType<typeof selectLineItems>,
  outletId: string | undefined,
  taxCodes: Record<string, string> = {},
  saleDisplayDiscount: string | number = 0,
  saleDiscountIsPercentage: boolean = true
): CalculateTaxInput | undefined => {
  if (!details?.shippingAddress) {
    return
  }

  if (!outletId) {
    return
  }

  const saleDiscountPercent = calculateSaleDiscountPercent(
    saleDisplayDiscount,
    saleDiscountIsPercentage,
    items
  )

  const freightItems = items.filter(isFreightLineItem)
  const nonFreightItems = items.filter(
    item =>
      item.fulfilmentType === FulfilmentType.DELIVERY &&
      !item.customerTaxApplied &&
      !isFreightLineItem(item)
  )

  if (nonFreightItems.length === 0) {
    return
  }

  const deliveryLineItems: TaxLineItem[] = nonFreightItems.map(item => {
    const itemDiscount = multiply(item.displayDiscount ?? 0, item.quantity)
    // The sale-level discount is distributed proportionally based on the
    // item's adjusted price (after item-level discounts)
    const saleDiscountShare = multiply(
      saleDiscountPercent,
      multiply(item.price, item.quantity)
    )
    // TaxJar API expects the total discount across all units, including
    // the line item's share of any sale-level discount
    const totalDiscount = add(itemDiscount, saleDiscountShare)

    return {
      id: item.id || item.localId,
      quantity: item.quantity.toString(),
      taxCode: taxCodes[item.product?.id] ?? '',
      unitPrice: item.basePrice.toString(),
      discount: totalDiscount.toString(),
    }
  })

  const shippingAmount = freightItems.reduce(
    (sum, item) =>
      add(
        sum,
        subtract(
          multiply(item.basePrice, item.quantity),
          item.displayDiscount ?? 0
        )
      ),
    0
  )

  return {
    destinationAddress: mapAddressToTaxAddress(details.shippingAddress),
    shippingAmount: shippingAmount.toString(),
    lineItems: deliveryLineItems,
    outletId,
  }
}

/**
 * Applies calculated taxes to delivery line items
 */
const applyTaxesToLineItems = (
  taxResponse: ResponseLineItem[],
  lineItems: LineItem[],
  stateName: string
): LineItem[] => {
  if (!taxResponse || taxResponse.length === 0) {
    return []
  }

  const taxUpdates = new Map(taxResponse.map(item => [item.id, item.taxes]))

  const updatedLineItems: LineItem[] = []

  lineItems.forEach(lineItem => {
    const taxes = taxUpdates.get(lineItem.id || lineItem.localId)
    if (
      taxes &&
      taxes.length > 0 &&
      lineItem.fulfilmentType === FulfilmentType.DELIVERY &&
      !lineItem.customerTaxApplied
    ) {
      // Calculate total tax
      const totalTax = taxes.reduce(
        (sum, tax) => add(sum, parseFloat(tax.totalTax)),
        0
      )

      const combinedRate = multiply(
        taxes.reduce((sum, tax) => add(sum, parseFloat(tax.rate)), 0),
        100
      )
      const groupName = `${stateName} sales tax (${combinedRate}%)`

      const groupId = taxes[0].groupRateId

      const taxInfo: ApiTax = {
        id: groupId,
        name: groupName,
        version: 1,
        deleted_at: null,
        is_default: false,
        display_name: groupName,
        rates: taxes.map(tax => ({
          id: tax.rateId,
          name: tax.rateName,
          rate: parseFloat(tax.rate),
          rules: [],
          display_name: tax.rateName,
        })),
      }

      const updatedLineItem: LineItem = {
        ...lineItem,
        tax: totalTax.toString(),
        baseTax: totalTax.toString(),
        taxInfo,
        taxComponents: taxes.map(tax => ({
          rate_id: tax.rateId,
          total_tax: parseFloat(tax.totalTax),
          rate: {
            id: tax.rateId,
            name: tax.rateName,
            rate: parseFloat(tax.rate),
            rules: [],
            display_name: tax.rateName,
          },
          total_tax_after_sale_discount: parseFloat(tax.totalTax),
          base_total_tax: parseFloat(tax.totalTax),
        })),
      }

      updatedLineItems.push(updatedLineItem)
    }
  })

  return updatedLineItems
}

export const useAutoTax = (): AutoTax => {
  const { isDeliveryTaxEnabled } = useAutotaxSettings()
  const { getLineItemFulfillmentAddressDetails } = useFulfillmentHelper()

  const fulfillmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const lineItems = useAppSelector(selectLineItems)
  const isLoading = useAppSelector(selectAutoTaxCalculating)
  const lastCalculatedKey = useAppSelector(selectAutoTaxLastCalculatedKey)
  const calculateTaxError = useAppSelector(selectCalculateTaxError)
  const inactiveCategoryLineItemIds = useAppSelector(
    selectAutoTaxInactiveCategoryLineItemIds
  )
  const outletId = useAppSelector(selectActiveOutletId)
  const saleDisplayDiscount = useAppSelector(selectSaleDisplayDiscount)
  const saleDiscountIsPercentage = useAppSelector(
    selectSaleDiscountIsPercentage
  )
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()
  const { fetchProductTaxCodes } = useProductTaxCodes()
  const customerTax = useCustomerTaxes()

  // Apply customer's custom tax to delivery line items that don't already have it
  useEffect(() => {
    if (!isDeliveryTaxEnabled) {
      return
    }
    const updatedLineItems = applyCustomerTaxToLineItems(customerTax, lineItems)
    if (updatedLineItems.length > 0) {
      dispatch(updateLineItems(updatedLineItems))
      saleManager.queueSaleCalculation()
    }
  }, [isDeliveryTaxEnabled, customerTax, lineItems, dispatch, saleManager])

  const isAutoTaxApplied = useMemo(() => {
    if (!isDeliveryTaxEnabled) {
      return false
    }

    if (currentOutlet?.physical_country_id !== US_COUNTRY_CODE) {
      return false
    }

    const isDelivery = fulfillmentDetail?.type === FulfilmentType.DELIVERY
    if (!isDelivery) {
      return false
    }

    if (fulfillmentDetail?.shippingAddress?.country_id !== US_COUNTRY_CODE) {
      return false
    }

    const deliveryItems = lineItems.filter(
      item =>
        item.fulfilmentType === FulfilmentType.DELIVERY &&
        !isFreightLineItem(item)
    )
    if (deliveryItems.length === 0) {
      return false
    }

    // If the customer has a custom tax override, it should take precedence
    // over auto-tax. We check IDB directly because the customerTaxApplied flag
    // on line items is not set when the product has an outlet-level tax.
    if (customerTax) {
      return false
    }

    const addressDetails = getLineItemFulfillmentAddressDetails(
      fulfillmentDetail
    )
    const hasValidAddresses =
      addressDetails.error === '' &&
      addressDetails.status ===
        AddressErrorsStatus.CORRECTSHIPPING_CORRECTBILLING

    return hasValidAddresses
  }, [
    isDeliveryTaxEnabled,
    currentOutlet,
    fulfillmentDetail,
    lineItems,
    customerTax,
    getLineItemFulfillmentAddressDetails,
  ])

  const saleFinals = useAppSelector(selectSaleFinals)
  const preAutoTaxFinals = useAppSelector(selectPreAutoTaxFinals)
  const preAutoTaxLineItems = useAppSelector(selectPreAutoTaxLineItems)

  /**
   * Restores line items to their pre-auto-tax state (original manual tax or zero)
   * and recalculates sale finals accordingly.
   * Used when auto-tax needs to be rolled back (API error or fulfillment type change).
   */
  const restoreAutoTaxLineItems = useCallback(() => {
    if (!preAutoTaxFinals) {
      return
    }

    const restoredLineItems = lineItems.map(lineItem => {
      const originalTaxInfo = preAutoTaxLineItems[lineItem.localId]
      if (originalTaxInfo) {
        return {
          ...lineItem,
          tax: originalTaxInfo.tax,
          baseTax: originalTaxInfo.baseTax,
          taxInfo: originalTaxInfo.taxInfo,
          taxComponents: originalTaxInfo.taxComponents,
        }
      }
      return lineItem
    })

    const hasChanges = restoredLineItems.some(
      (item, idx) => item !== lineItems[idx]
    )

    if (hasChanges) {
      dispatch(updateLineItems(restoredLineItems))

      saleManager.queueSaleCalculation()
    }
  }, [preAutoTaxFinals, preAutoTaxLineItems, lineItems, saleManager, dispatch])

  const [calculateTax] = useMutation<
    CalculateTaxResponse,
    { input: CalculateTaxInput }
  >(CalculateTax, {
    onCompleted: data => {
      dispatch(setAutoTaxCalculating(false))

      if (data.calculateTax.error) {
        const error = data.calculateTax.error

        if (
          error.type === CalculateTaxErrorTypes.INACTIVE_TAX_CATEGORY &&
          error.lineItemIds
        ) {
          dispatch(setAutoTaxInactiveCategoryLineItemIds(error.lineItemIds))
        } else {
          dispatch(setCalculateTaxError(error.type))
          dispatch(setAutoTaxInactiveCategoryLineItemIds(undefined))
        }

        restoreAutoTaxLineItems()
        return
      }

      dispatch(setCalculateTaxError(undefined))
      dispatch(setAutoTaxInactiveCategoryLineItemIds(undefined))

      syncTaxGroupsIfMissing(data.calculateTax.lineItems)

      // Read the CURRENT Redux state directly from the store, bypassing React's
      // render cycle. This is critical: if customer tax re-pricing completed while
      // the auto-tax API call was in flight, the closure values (lineItems etc.)
      // would be stale and we'd overwrite customer tax with auto-tax.
      const currentState = store.getState()
      const currentLineItems = selectLineItems(currentState)
      const currentFulfillmentDetail = selectSaleFulfilmentDetail(currentState)

      // If any delivery item now has customer tax applied, discard auto-tax result
      const hasCustomerTax = currentLineItems.some(
        item =>
          item.fulfilmentType === FulfilmentType.DELIVERY &&
          item.customerTaxApplied
      )
      if (hasCustomerTax) {
        return
      }

      // Track that we're using auto-tax (for restoration later)
      if (!preAutoTaxFinals) {
        // Store a flag to indicate auto-tax was applied
        // We don't store the actual finals because they can become stale
        dispatch(setPreAutoTaxFinals(saleFinals))
      }

      const stateCode =
        currentFulfillmentDetail?.shippingAddress?.state?.toUpperCase() || ''
      const stateEntry = AMERICAN_STATES.find(
        (s: { code: string; name: string }) => s.code === stateCode
      )
      const stateName = stateEntry ? stateEntry.name : stateCode

      const updatedLineItems = applyTaxesToLineItems(
        data.calculateTax.lineItems,
        currentLineItems,
        stateName
      )

      if (updatedLineItems.length > 0) {
        // Store original tax info for delivery line items before auto-tax replaces them
        const originalTaxInfo: Record<
          string,
          {
            tax: string
            baseTax: string
            taxInfo?: ApiTax
            taxComponents: LineItemTaxComponent[]
          }
        > = { ...preAutoTaxLineItems }

        updatedLineItems.forEach(updatedItem => {
          const originalItem = currentLineItems.find(
            item => item.localId === updatedItem.localId
          )
          // If we found the original item and haven't stored its tax info yet, save it
          // Note: updatedLineItems only contains delivery items that received auto-tax
          if (originalItem && !originalTaxInfo[originalItem.localId]) {
            originalTaxInfo[originalItem.localId] = {
              tax: String(originalItem.tax),
              baseTax: String(originalItem.baseTax),
              taxInfo: originalItem.taxInfo,
              taxComponents: originalItem.taxComponents || [],
            }
          }
        })

        dispatch(setPreAutoTaxLineItems(originalTaxInfo))
        dispatch(updateLineItems(updatedLineItems))
        saleManager.queueSaleCalculation()
      }
    },
    onError: () => {
      dispatch(setAutoTaxCalculating(false))
      // TODO: Add taxes rates to the indexDB and do offline calculation
      dispatch(setCalculateTaxError(CalculateTaxErrorTypes.UNSPECIFIED))
      dispatch(setAutoTaxInactiveCategoryLineItemIds(undefined))
      restoreAutoTaxLineItems()
    },
  })

  // Create a stable key that only changes when actual input changes
  const inputKey = useMemo(() => {
    if (!isAutoTaxApplied) {
      return ''
    }

    const input = buildTaxInput(
      fulfillmentDetail,
      lineItems,
      outletId,
      {},
      saleDisplayDiscount,
      saleDiscountIsPercentage
    )
    if (!input) {
      return ''
    }

    return JSON.stringify({
      address: input.destinationAddress,
      items: input.lineItems
        .map(item => ({
          id: item.id,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount,
        }))
        .sort((a, b) => a.id.localeCompare(b.id)),
      shippingAmount: input.shippingAmount,
    })
  }, [
    isAutoTaxApplied,
    fulfillmentDetail,
    lineItems,
    outletId,
    saleDisplayDiscount,
    saleDiscountIsPercentage,
  ])

  // Track the latest isAutoTaxApplied so the debounced callback can read it
  const isAutoTaxAppliedRef = useRef(isAutoTaxApplied)
  isAutoTaxAppliedRef.current = isAutoTaxApplied

  // Debounced function to trigger tax calculation
  const calculateTaxDebounced = useRef<ReturnType<typeof debounce>>()

  // Initialize debounced function once
  if (!calculateTaxDebounced.current) {
    calculateTaxDebounced.current = debounce(
      (key: string, input: CalculateTaxInput, currentLastKey: string) => {
        if (!key || currentLastKey === key || !isAutoTaxAppliedRef.current) {
          return
        }

        dispatch(setCalculateTaxError(undefined))
        dispatch(setAutoTaxLastCalculatedKey(key))
        dispatch(setAutoTaxCalculating(true))
        calculateTax({ variables: { input } })
      },
      DEBOUNCE_OPTIONS.wait,
      { maxWait: DEBOUNCE_OPTIONS.maxWait }
    )
  }

  // Trigger calculation when inputKey changes
  useEffect(() => {
    if (!inputKey) {
      return
    }

    const deliveryItems = lineItems.filter(
      item => item.fulfilmentType === FulfilmentType.DELIVERY
    )

    fetchProductTaxCodes(deliveryItems).then(taxCodes => {
      const input = buildTaxInput(
        fulfillmentDetail,
        lineItems,
        outletId,
        taxCodes,
        saleDisplayDiscount,
        saleDiscountIsPercentage
      )
      if (input) {
        calculateTaxDebounced.current?.(inputKey, input, lastCalculatedKey)
      }
    })
    // Only depend on inputKey and lastCalculatedKey
    // fulfillmentDetail, lineItems, and fetchProductTaxCodes are already captured in inputKey
    // dispatch and calculateTax are stable references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey, lastCalculatedKey])

  // Cleanup: cancel any pending debounced calls when component unmounts
  useEffect(() => {
    return () => {
      calculateTaxDebounced.current?.cancel()
    }
  }, [])

  // Restore original taxes when fulfillment type changes away from delivery
  useEffect(() => {
    // Only restore if auto-tax features are enabled, auto-tax is no longer
    // active, and we have stored pre-auto-tax state to restore from
    if (!isDeliveryTaxEnabled || isAutoTaxApplied || !preAutoTaxFinals) {
      return
    }

    // Cancel any pending auto-tax calculations
    calculateTaxDebounced.current?.cancel()
    dispatch(setAutoTaxCalculating(false))

    // Remove auto-tax from line items and restore original tax info
    const restoredLineItems = lineItems.map(lineItem => {
      const originalTaxInfo = preAutoTaxLineItems[lineItem.localId]
      if (originalTaxInfo) {
        return {
          ...lineItem,
          tax: originalTaxInfo.tax,
          baseTax: originalTaxInfo.baseTax,
          taxInfo: originalTaxInfo.taxInfo,
          taxComponents: originalTaxInfo.taxComponents,
        }
      }
      return lineItem
    })

    // Clear the auto-tax tracking state before recalculation so that
    // downstream calculations (e.g. _totalSalePipeline) see consistent state
    dispatch(setPreAutoTaxFinals(null))
    dispatch(setPreAutoTaxLineItems({}))
    dispatch(setAutoTaxLastCalculatedKey(''))
    dispatch(setCalculateTaxError(undefined))
    dispatch(setAutoTaxInactiveCategoryLineItemIds(undefined))

    // Check if any line items changed
    const hasChanges = restoredLineItems.some(
      (item, idx) => item !== lineItems[idx]
    )

    if (hasChanges) {
      dispatch(updateLineItems(restoredLineItems))

      saleManager.queueSaleCalculation()
    }
  }, [
    isDeliveryTaxEnabled,
    isAutoTaxApplied,
    preAutoTaxFinals,
    preAutoTaxLineItems,
    lineItems,
    saleManager,
    dispatch,
  ])

  return {
    isAutoTaxApplied,
    isLoading,
    hasCalculateTaxError: !!calculateTaxError,
    inactiveCategoryLineItemIds,
  }
}
